import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "../store";
import { UserState } from "../store/reducers/user-reducer";
import { Cookie } from "../services/storage";
import { SHOW_SURVEY, USER_SURVEY_SKIP } from "../constants/storage";
import { actionSetUserSurveyHasError } from "../store/actions/user-actions";
import { useEffect, useState, useCallback } from "react";
import { SystemState } from "../store/reducers/site-reducer";

/**
 * This hook centralizes all survey modal logic including:
 * - Cookie-based modal triggering
 * - User skip tracking
 * - Modal state management
 * - Integration with Redux store for user and system properties
 * 
 * @returns {Object} Survey modal controls and state
 * @returns {Function} handleSurveyOnClose - Function to handle modal close event
 * @returns {Function} setSurveyModalOpen - Function to manually set modal open state
 * @returns {boolean} surveyModalOpen - Current modal open state
 */
export const useSurveyModal = () => {
  const userState = useSelector<ApplicationState, UserState>(
    (state) => state.user
  );
  const { sysProps } = useSelector<ApplicationState, SystemState>(
    (state) => state.system
  );
  const dispatch = useDispatch();

  // Local state for modal visibility
  const [surveyModalOpen, setSurveyModalOpen] = useState(false);
  
  // Get current survey cookie value
  const showSurveyCookie = Cookie.get(SHOW_SURVEY);

  /**
   * Handles the survey modal close event
   * 
   * Performs the following actions:
   * 1. Adds current user to the skipped users list if not already present
   * 2. Updates the skip user cookie
   * 3. Closes the modal
   * 4. Removes the survey trigger cookie
   * 5. Resets any survey error state in Redux
   */
  const handleSurveyOnClose = useCallback(() => {
    const userLogin = userState.userData.loginId;
    
    // Get existing skipped users or initialize empty array
    const skippedUser = Cookie.get(USER_SURVEY_SKIP) || "[]";
    const skippedUserArr = JSON.parse(skippedUser);

    // Check if user is already in the skipped list
    const userDidSkip = skippedUserArr.includes(userLogin);
    
    // Add user to skipped list if not already there
    if (!userDidSkip) {
      skippedUserArr.push(userLogin);
      Cookie.set(USER_SURVEY_SKIP, JSON.stringify(skippedUserArr));
    }
    
    // Close modal and clean up cookie
    setSurveyModalOpen(false);
    Cookie.remove(SHOW_SURVEY);
    
    // Reset survey error state
    dispatch(actionSetUserSurveyHasError(false));
  }, [userState.userData.loginId, dispatch]);

  /**
   * Checks conditions and shows survey modal if appropriate
   * 
   * Conditions for showing modal:
   * 1. Survey form is enabled in system properties
   * 2. open modal when SHOW_SURVEY is set to true
   */
  const handleShowSurveyModal = useCallback(() => {
    // Don't show modal if survey is disabled in SPAP
    if (!sysProps.SURVEY_FORM) return;

    // Check if SHOW_SURVEY sets to true open surveyModal
    if (Cookie.get(SHOW_SURVEY) === "true") {
      setSurveyModalOpen(true);
    }
  }, [sysProps.SURVEY_FORM]);

  useEffect(() => {
    handleShowSurveyModal();
  }, [showSurveyCookie, sysProps.SURVEY_FORM, handleShowSurveyModal]);

  /**
   * Effect hook to automatically close modal when survey is submitted
   * 
   * Triggers when:
   * - User successfully submits survey (hasAnsweredSurvey becomes true)
   * 
   * Performs:
   * - Closes the modal
   * - Removes SHOW_SURVEY cookie
   */
  useEffect(() => {
    if (userState.userSurvey.hasAnsweredSurvey) {
      setSurveyModalOpen(false);
      Cookie.remove(SHOW_SURVEY);
    }
  }, [userState.userSurvey.hasAnsweredSurvey]);

  return { 
    handleSurveyOnClose, 
    setSurveyModalOpen, 
    surveyModalOpen 
  };
};
